import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { DesignationField } from 'components/WorkspaceEditor/features/drawingDesignation'
import { parseDesignationText } from 'components/WorkspaceEditor/features/drawingDesignation/services/textParser'
import React, { memo, useMemo } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { ContentEditorProps } from '../../ContentEditor'
import { withLazyLoad } from '../../withLazyComponent'
import { KeyedText } from './KeyedText'

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/** Props for designation field (for Product Type nodes) */
type DesignationProps = {
    /** Whether this is a Product Type node (for drawing designation field) */
    isProductType?: boolean
    /** Content item ID for drawing designation */
    contentItemId?: string
    /** Node ID for drawing designation */
    nodeId?: string
}

/** Props for the text editor component */
interface EditorContentProps extends ContentEditorProps {
    /** Whether this text has a label prefix (like "Title:") */
    isKeyedText: boolean
    /** The label text that appears before the editable portion */
    keyedText: string
    /** The editable text portion after the label */
    keyedTextValue?: string
    /** CSS styling classes */
    classes?: string
    /** Designation field props (for Product Type nodes) */
    designationProps?: DesignationProps
}

/**
 * A text editor that handles two display formats:
 * 1. Regular text editing (standard mode)
 * 2. Label-value pairs where the label (with semicolon) is fixed and only the value is editable
 *
 * Content team members always see the standard editor regardless of format.
 *
 * @param {EditorContentProps} props - Props for the component.
 */
export const EditorContent = memo<EditorContentProps>(
    ({
        initialData,
        initialSuggestions,
        clickCoordinates,
        handlers,
        isKeyedText,
        keyedText,
        keyedTextValue,
        classes,
        designationProps,
    }) => {
        const isContentTeamFromStudioPage = useShallowSelector(
            (state) => state.isContentTeamFromStudioPage
        )
        const { previewFeatures } = usePreviewFeatures()
        const { isProductType, contentItemId, nodeId } = designationProps || {}

        // For Product Types, parse keyedText
        const { textPrefix } = useMemo(() => {
            if (!isProductType || !isKeyedText) {
                return { textPrefix: keyedText, designationValue: '' }
            }

            const parsed = parseDesignationText(keyedText)
            return {
                textPrefix: parsed.prefix,
                designationValue: parsed.designation
            }
        }, [keyedText, isProductType, isKeyedText])

        const editorComponent = (
            <ContentEditor
                initialData={initialData}
                initialSuggestions={initialSuggestions}
                clickCoordinates={clickCoordinates}
                handlers={{
                    onReady: handlers?.onReady,
                    onBlur: handlers?.onBlur,
                    onChange: handlers?.onChange,
                }}
            />
        )

        // show standard editor, for regular text or from OM/SMM content
        if (!isKeyedText || isContentTeamFromStudioPage) {
            return editorComponent
        }

        // show label separately from editable value, for keyed text
        return (
            <>
                <KeyedText
                    keyedText={previewFeatures.hasDrawingDesignation ? textPrefix : keyedText}
                    classes={classes}
                    data-testid="keyed-text-key"
                />
                {previewFeatures.hasDrawingDesignation &&
                    isProductType &&
                    contentItemId &&
                    nodeId && (
                        <>
                            <DesignationField
                                keyedText={keyedText}
                                keyedTextValue={keyedTextValue}
                                contentItemId={contentItemId}
                                editable={true}
                                inactive={false}
                                showPrefix={false}
                                showSuffix={false}
                            />
                            {':'}
                        </>
                    )}
                {keyedTextValue && editorComponent}
            </>
        )
    }
)
